import {Component, computed, effect, ElementRef, inject, Injector, input, InputSignal, Signal, viewChild} from '@angular/core';
import {NgComponentOutlet} from '@angular/common';
import {AppModalType} from '../../../types/modal/app-modal.type';
import {ModalService} from '../../../services/modal/modal.service';
import {APP_MODAL_REF} from '../../../constants/modal/app-modal-ref.constant';

@Component({
    host: {
        '[class]': 'this.hostClasses()'
    },
    selector: 'app-modal-host',
    imports: [
        NgComponentOutlet
    ],
    templateUrl: './modal-host.component.html',
    styleUrl: './modal-host.component.css',
})
export class ModalHostComponent {
    public modal: InputSignal<AppModalType> = input.required();
    public zIndex: InputSignal<number> = input.required();
    protected hostClasses: Signal<string>;
    protected modalInjector: Signal<Injector>;
    private readonly dialogElement: Signal<ElementRef<HTMLDialogElement> | undefined> = viewChild('dialogElement');
    private readonly injector = inject(Injector);
    private readonly modalService = inject(ModalService);
    private hasMouseDownOnBackdrop: boolean = false;

    public constructor() {
        this.hostClasses = computed(() => {
            const modal = this.modal();
            const backdropClass = modal.hasBackdrop ? 'has-backdrop' : 'no-backdrop';
            return `vertical-${modal.verticalPosition} horizontal-${modal.horizontalPosition} ${backdropClass}`;
        });
        this.modalInjector = computed(() => Injector.create({
            parent: this.injector,
            providers: [
                {
                    provide: APP_MODAL_REF,
                    useValue: this.modal()
                }
            ]
        }));
        effect(() => {
            this.showDialogElement();
        });
    }

    /**
     * The browser only dispatches the cancel event while the document still has transient user activation, so a second escape press would close the
     * dialog element without it. Suppressing the key press itself stops the close request before it is turned into a close watcher request.
     */
    protected onKeyDown(event: KeyboardEvent): void {
        if (event.key !== 'Escape' || this.modal().shouldCloseOnEscapeKey) {
            return;
        }
        event.preventDefault();
    }

    /**
     * Catches the close requests that do not come from a key press, such as the Android back button.
     */
    protected onCancel(event: Event): void {
        if (this.modal().shouldCloseOnEscapeKey) {
            return;
        }
        event.preventDefault();
    }

    /**
     * The native dialog element was closed from the outside (e.g. escape key), so there is no return data.
     */
    protected onClose(): void {
        this.modalService.closeModal(this.modal());
    }

    protected onDialogMouseDown(event: MouseEvent): void {
        this.hasMouseDownOnBackdrop = this.isEventOnBackdrop(event);
    }

    /**
     * A click is only treated as a backdrop click when it both started and ended on the backdrop, so dragging a selection out of the dialog does not close it.
     */
    protected onDialogClick(event: MouseEvent): void {
        const hasMouseDownOnBackdrop = this.hasMouseDownOnBackdrop;
        this.hasMouseDownOnBackdrop = false;
        const modal = this.modal();
        if (!modal.hasBackdrop || !modal.shouldCloseOnBackdropClick || !hasMouseDownOnBackdrop || !this.isEventOnBackdrop(event)) {
            return;
        }
        this.modalService.closeModal(modal);
    }

    private isEventOnBackdrop(event: MouseEvent): boolean {
        return event.target === this.dialogElement()?.nativeElement;
    }

    private showDialogElement(): void {
        const dialogElement = this.dialogElement()?.nativeElement;
        const modal = this.modal();
        if (!dialogElement || dialogElement.open) {
            return;
        }
        const previouslyFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : undefined;
        try {
            if (modal.isModal && modal.shouldAutoFocus) {
                dialogElement.showModal();
            } else if (modal.isModal) {
                dialogElement.setAttribute('autofocus', '');
                dialogElement.inert = true;
                dialogElement.showModal();
                dialogElement.inert = false;
                dialogElement.focus();
            } else if (modal.shouldAutoFocus) {
                dialogElement.show();
            } else {
                // Setting the open attribute displays the dialogElement without running the dialogElement focusing steps, so the focus stays where it is.
                dialogElement.open = true;
            }
            if (!modal.isModal && !modal.shouldAutoFocus && previouslyFocusedElement && previouslyFocusedElement !== document.body && document.activeElement !== previouslyFocusedElement) {
                previouslyFocusedElement.focus();
            }
        } catch (error) {
            console.error('Failed to open modal dialogElement', error);
        }
    }
}
